/**
 * SageEditModal — edit a Sage-LANDED order and push the change to Sage.
 *
 * Opened from OrderDetailModal's conditional "Edit in Sage" button (only shown
 * when orderDetail.editable_in_sage is true — i.e. the order still exists as an
 * open SO in Sage). Lets the operator change line qty/price (with the same
 * pricing-source dropdown the draft editor has — SP / open quotes / standard),
 * key header fields (ship-via with live freight quotes), and the ship-to
 * address (pick an existing Sage ship-to, or create a new one in Sage via
 * /sage-ship-to and use it), previews the old→new diff, then POSTs
 * /portal/orders/{id}/sage-edit (→ salesOrderUpdate). Also renders the order's
 * Sage write history (audit view over sage_writes). Self-contained +
 * inline-styled to avoid coupling to the 4.5k-line OrderDetailModal.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  portalOrderService,
  type LineItemPricingContext,
  type OrderLineComments,
  type OrderPricingContextResponse,
  type OrderStagingStatus,
  type PortalOrderDetailResponse,
  type SageFieldChangePayload,
  type SageLineAddPayload,
  type SageLineUpdatePayload,
  type SageOrderEditPayload,
  type SageWriteAuditEntry,
  type SubmissionFieldError,
  type WarehouseOption,
  type WeightSummaryResponse,
} from '../../services/portalOrders';
import {
  portalProductService,
  type ProductSearchResult,
} from '../../services/portalProducts';
import {
  calculateLineWeight,
  parseWeight,
  resolveShipWeight,
} from '../../utils/formatting';
import ShipToSearch, { type ShipToResult } from './ShipToSearch';
import NewSageShipToForm, { type CreatedShipTo } from './NewSageShipToForm';
import FreightQuoteModal from './FreightQuoteModal';
import KitComponentBadge from './KitComponentBadge';
import StagedNoticeRecipientsList from './StagedNoticeRecipientsList';
import BrandAliasBadge from './BrandAliasBadge';
import PricingSourcePicker from './PricingSourcePicker';
import SearchableDropdown from './SearchableDropdown';
import { buildWarehouseDropdownItems } from '../../data/warehouseDropdownItems';
import {
  BILL_OF_LADING_OPTIONS,
  DEFAULT_BILL_OF_LADING,
  billOfLadingLabel,
  customerPaysCarrier,
} from './billOfLading';
import { findWarehouseByCode } from '../../data/sageWarehouses';
import { pricingItemFromLookup } from './addLinePricing';
import { matchLineCommentBundles } from './lineCommentBundles';
import {
  restagePricingItem,
  sageCommentOutcome,
  sameFieldValue,
  shipToLabel,
  shipmentAffectingChanges,
  shortDateLabel,
  stagedSinceLabel,
  warehouseLabel,
  weightThresholdAdvisory,
  type ShipToJson,
} from './sageEditUtils';
import { formatChangeValue, formatSettledLinePrice } from './sagePendingWrites';

/** "$12.50" for the pricing-source picker; a dash for blank/invalid. */
const formatPrice = (val: string | number | null | undefined): string => {
  if (val === null || val === undefined) return '-';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (Number.isNaN(num)) return '-';
  return `$${num.toFixed(2)}`;
};

/** Sage edit history value: blank shows as a dash, dict payloads as text. */
const historyValue = (v: unknown): string => {
  const text = formatChangeValue(v);
  return text === '(blank)' ? '—' : text;
};

interface Props {
  orderDetail: PortalOrderDetailResponse;
  onClose: () => void;
  onSaved: () => void;
}

interface LineDraft {
  id: string;
  itemCode: string;
  description: string;
  qty: string;
  price: string;
  origQty: string;
  origPrice: string;
  /** Staged for removal — excluded from updates, sent as a line_remove. */
  removed: boolean;
  /**
   * What the operator is ADDING to the Sage comment (never a replacement).
   * The comment already on the line is NOT kept here: it is derived from the
   * `/line-comments` mirror (`existingCommentByLineId`), because the order's
   * own `sage_comment_text` is only what we last sent and diverges from Sage
   * after the first append.
   */
  commentAdd: string;
}

/**
 * A brand-new line being added to a Sage-landed order. `priceLocked` is true
 * when the pricing waterfall returned an authoritative price (seeded, read-only
 * unless the operator overrides); false when the waterfall could not resolve a
 * price (source === 'investigation') and the operator MUST key it — we never
 * seed a guessed price (see the AU00249 standard-price-fallback bug class).
 */
interface AddedLine {
  tempId: string;
  itemCode: string;
  description: string;
  qty: string;
  price: string;
  priceResolved: boolean;
  /** Pricing classification from the add-time lookup (SP / quotes / Std for
   *  THIS item), so the staged row's PricingSourcePicker has options even
   *  though the item is not in the order's pricing context yet. */
  pricing: LineItemPricingContext | null;
  /** Per-unit ship weight from the product search row (lbs), for the live
   *  local weight sum. Null when the catalog has no weight on file. */
  unitWeight: number | null;
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200,
  } as React.CSSProperties,
  modal: {
    background: '#fff', borderRadius: 12, width: 'min(680px, 94vw)',
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.35)',
  } as React.CSSProperties,
  head: {
    padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  } as React.CSSProperties,
  title: { fontSize: 16, fontWeight: 700, color: '#111827' } as React.CSSProperties,
  sub: { fontSize: 12, color: '#6b7280', marginTop: 2 } as React.CSSProperties,
  body: { padding: '16px 20px' } as React.CSSProperties,
  row: { display: 'flex', gap: 10, alignItems: 'center', margin: '8px 0' } as React.CSSProperties,
  label: { flex: '0 0 120px', fontSize: 12.5, color: '#6b7280' } as React.CSSProperties,
  input: {
    padding: '6px 9px', border: '1px solid #d1d5db', borderRadius: 7,
    fontSize: 13, color: '#111827', width: 130,
  } as React.CSSProperties,
  th: { textAlign: 'left', fontSize: 11.5, color: '#6b7280', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' } as React.CSSProperties,
  td: { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontSize: 13 } as React.CSSProperties,
  diff: { fontSize: 12.5, color: '#374151', margin: '4px 0' } as React.CSSProperties,
  old: { color: '#dc2626', textDecoration: 'line-through' } as React.CSSProperties,
  neu: { color: '#059669', fontWeight: 600 } as React.CSSProperties,
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px', borderTop: '1px solid #e5e7eb' } as React.CSSProperties,
  btn: { padding: '8px 14px', borderRadius: 8, fontSize: 13, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' } as React.CSSProperties,
  primary: { background: '#2563eb', borderColor: '#2563eb', color: '#fff', fontWeight: 600 } as React.CSSProperties,
  smallBtn: {
    padding: '4px 10px', borderRadius: 7, fontSize: 12, border: '1px solid #d1d5db',
    background: '#fff', color: '#374151', cursor: 'pointer',
  } as React.CSSProperties,
  shipToBox: {
    flex: 1, fontSize: 13, color: '#111827', display: 'flex',
    alignItems: 'center', gap: 8, flexWrap: 'wrap',
  } as React.CSSProperties,
  panel: {
    border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px',
    margin: '8px 0 8px 130px', background: '#f9fafb',
  } as React.CSSProperties,
  formGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6,
  } as React.CSSProperties,
  histStatus: {
    fontSize: 11, fontWeight: 600, borderRadius: 999, padding: '2px 8px',
  } as React.CSSProperties,
};

const HIST_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  pending: { bg: '#f3f4f6', fg: '#6b7280' },
  submitted: { bg: '#fef3c7', fg: '#92400e' },
  confirmed: { bg: '#d1fae5', fg: '#065f46' },
  failed: { bg: '#fee2e2', fg: '#991b1b' },
};

const KIND_LABELS: Record<string, string> = {
  order_update: 'Order update',
  ship_to_create: 'Ship-to create',
  customer_create: 'Customer create',
};

const SOURCE_LABELS: Record<string, string> = {
  manual: 'manual edit',
  special_pricing_change: 'special-pricing policy',
  system: 'system',
  merlin_proposed: 'Merlin proposal',
  auto_retry: 'auto-retry (record lock)',
};

export const SageEditModal: React.FC<Props> = ({ orderDetail, onClose, onSaved }) => {
  const order = orderDetail.order;
  const [lines, setLines] = useState<LineDraft[]>(() =>
    orderDetail.line_items.map((li) => ({
      id: li.id,
      itemCode: li.item_code,
      description: li.item_description ?? '',
      qty: li.quantity != null ? String(li.quantity) : '',
      price: li.unit_price != null ? String(li.unit_price) : '',
      origQty: li.quantity != null ? String(li.quantity) : '',
      origPrice: li.unit_price != null ? String(li.unit_price) : '',
      removed: false,
      commentAdd: '',
    })),
  );
  const [shipVia, setShipVia] = useState<string>((order.ship_via as string) ?? '');
  const origShipVia = ((order.ship_via as string) ?? '');
  // Live carrier quotes (same modal + endpoint as the draft editor). Rates
  // the SAVED order — see the note rendered beside the button.
  const [showFreight, setShowFreight] = useState(false);
  const [poNumber, setPoNumber] = useState<string>((order.po_number as string) ?? '');
  const origPo = ((order.po_number as string) ?? '');

  // ── Bill of lading (header) ──
  // Who pays the carrier: Prepaid / Collect / 3rd Party (Sage UDF_BL). The
  // draft editor had this control; once the order was in Sage the only way
  // to change it was by hand in Sage, so a carrier switched to a collect
  // carrier (AU02529, Siegel -> CENTRAL) shipped with a prepaid BOL (kanban
  // eb79e8dd). Seeds from Sage's CURRENT term (sage_freight.udf_bl) for the
  // same reason as the warehouse: the desk changes it in Sage and the
  // portal row never learns.
  const origUdfBl = (
    (orderDetail.sage_freight?.udf_bl as string | null | undefined)
    || (order.udf_bl as string | null | undefined)
    || DEFAULT_BILL_OF_LADING
  );
  const [udfBl, setUdfBl] = useState<string>(origUdfBl);

  // ── Warehouse (header) ──
  // Same option source + component as CreateOrderDialog. '000' is the
  // "unspecified" sentinel, so a blank pick falls back to it rather than
  // staging ''. The field seeds from Sage's CURRENT warehouse
  // (sage_freight.warehouse_code, the open-SO mirror) and only falls back to
  // the portal's submitted value when the mirror has none: the desk
  // re-points confirmed SOs (000 -> 003 on Ferguson orders) and the portal
  // row goes stale, so seeding from it showed "000" on a 003 order and
  // audited an "old" value that was never Sage's (AU01971, kanban ad355384).
  // Only header WarehouseCode rides the salesOrderUpdate; the backend echoes
  // live Sage when this field is untouched.
  const origWarehouse = (
    (orderDetail.sage_freight?.warehouse_code as string | null | undefined)
    || (order.warehouse_code as string)
    || '000'
  );
  const [warehouseCode, setWarehouseCode] = useState<string>(origWarehouse);
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  const warehouseItems = useMemo(
    () => buildWarehouseDropdownItems(warehouseOptions),
    [warehouseOptions],
  );
  useEffect(() => {
    let cancelled = false;
    portalOrderService
      .getWarehouses()
      .then((opts) => { if (!cancelled) setWarehouseOptions(opts); })
      .catch(() => { if (!cancelled) setWarehouseOptions([]); });
    return () => { cancelled = true; };
  }, []);

  // ── Ship-to (edit scope 3) ──
  const origShipTo = (order.ship_to_address ?? null) as ShipToJson | null;
  const [shipToDraft, setShipToDraft] = useState<ShipToJson | null>(null);
  const [showShipToPicker, setShowShipToPicker] = useState(false);
  const [showNewShipTo, setShowNewShipTo] = useState(false);
  const [shipToCreatedNote, setShipToCreatedNote] = useState<string | null>(null);

  // ── Sage write history (audit view) ──
  const [writes, setWrites] = useState<SageWriteAuditEntry[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Count captured at push time: after onSaved() the parent refetches the
  // order and `changes` recomputes against the new baseline (→ 0), which
  // used to render "Pushed 0 changes" on a successful push.
  const [pushedCount, setPushedCount] = useState(0);

  // ── Staged-order guard (kanban 42a2ed0a) ──
  // Has the dock already packed this order? Read from the sales site's
  // Staged tick when the modal opens, so the CSR sees it before keying
  // anything. Null until loaded or when the read failed (the push re-checks
  // server-side either way).
  const [staging, setStaging] = useState<OrderStagingStatus | null>(null);
  // The override prompt: shown before a shipment-changing push on a staged
  // order (client pre-check) or when the server holds the edit with
  // `staging` set. Confirming re-sends with staged_override=true.
  const [stagedPrompt, setStagedPrompt] = useState(false);
  // Who this push will email: the ticked rows of the shared recipient list
  // (Scott, review ifevp1), preselected from the list the server returns.
  const [noticeRecipients, setNoticeRecipients] = useState<string[]>([]);
  // Once the CSR has ticked or untick­ed, a later staging read must not reset it.
  const noticeTouched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    portalOrderService
      .getStagingStatus(order.id)
      .then((st) => {
        if (cancelled) return;
        setStaging(st);
        if (!noticeTouched.current) setNoticeRecipients(st.notice_recipients ?? []);
      })
      .catch(() => { if (!cancelled) setStaging(null); });
    return () => { cancelled = true; };
  }, [order.id]);

  useEffect(() => {
    let cancelled = false;
    portalOrderService
      .getSageWrites(order.id)
      .then((rows) => { if (!cancelled) setWrites(rows); })
      .catch(() => { if (!cancelled) setWrites([]); });
    return () => { cancelled = true; };
  }, [order.id]);

  const setLine = (id: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  // ── Shipment weight (live, as the edit is staged) ──
  // Two sources, cheapest first: an instant local qty × unit-weight sum
  // (unit weights from the order's pricing context / the add-line search
  // row), and Sage's authoritative skid-inclusive envelope — the saved
  // baseline via GET /weight-summary, the staged set via the debounced
  // POST /weight-preview. The panel prefers the Sage pair and falls back
  // to the local pair when the freight API can't answer.
  const [unitWeights, setUnitWeights] = useState<Record<string, number>>({});
  // Full pricing context: per-line current_source + available_sources feed
  // the same PricingSourcePicker the draft editor renders (SP / Qt / Std).
  const [pricingCtx, setPricingCtx] = useState<OrderPricingContextResponse | null>(null);
  const [savedWeight, setSavedWeight] = useState<WeightSummaryResponse | null>(null);
  const [previewWeight, setPreviewWeight] = useState<WeightSummaryResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    portalOrderService
      .getPricingContext(order.id)
      .then((ctx) => {
        if (cancelled) return;
        setPricingCtx(ctx);
        const map: Record<string, number> = {};
        for (const li of ctx.line_items) {
          const w = parseWeight(resolveShipWeight(li));
          if (w != null) map[li.item_code] = w;
        }
        setUnitWeights(map);
      })
      .catch(() => { /* picker shows no options; local sum shows fewer lines */ });
    portalOrderService
      .getWeightSummary(order.id)
      .then((resp) => { if (!cancelled) setSavedWeight(resp); })
      .catch(() => { if (!cancelled) setSavedWeight(null); });
    return () => { cancelled = true; };
  }, [order.id]);

  // ── Add new line (scope: add a line to an order already in Sage) ──
  const customerNo = (order.customer_no as string) ?? null;
  // ── The comment ALREADY on each Sage line ──
  // Read from the sage_sales_order_lines mirror, not from the order's own
  // sage_comment_text: that field is only what the portal last SENT, and after
  // one append it holds just that addition while Sage holds `existing | add`.
  // Reasoning about "already present" against the stale outbound value would
  // let the preview promise an append that boi.php then silently discards —
  // the exact trap sageCommentOutcome exists to surface (kanban 099373a1).
  const [lineComments, setLineComments] = useState<OrderLineComments | null | 'failed'>(null);
  useEffect(() => {
    let cancelled = false;
    portalOrderService
      .getLineComments(order.id)
      .then((resp) => { if (!cancelled) setLineComments(resp); })
      .catch(() => { if (!cancelled) setLineComments('failed'); });
    return () => { cancelled = true; };
  }, [order.id]);
  const mirrorReady = lineComments !== null && lineComments !== 'failed' && lineComments.available;
  const mirrorSettled = lineComments !== null;
  const existingCommentByLineId = useMemo(() => {
    const out = new Map<string, string>();
    const bundles = mirrorReady ? (lineComments as OrderLineComments).bundles : null;
    const matched = matchLineCommentBundles(orderDetail.line_items, bundles);
    for (const li of orderDetail.line_items) {
      const fromMirror = matched.get(li.id)?.sage_comment_text;
      // Mirror wins. Fall back to what we last sent only when Sage cannot be
      // read; the row then says so, because that value may be behind.
      out.set(li.id, (mirrorReady ? fromMirror : li.sage_comment_text) ?? '');
    }
    return out;
  }, [lineComments, mirrorReady, orderDetail.line_items]);
  const existingCommentOf = (lineId: string): string => existingCommentByLineId.get(lineId) ?? '';

  const [added, setAdded] = useState<AddedLine[]>([]);
  const [showAddSearch, setShowAddSearch] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [addResults, setAddResults] = useState<ProductSearchResult[]>([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addSeeding, setAddSeeding] = useState(false);
  const nextTempId = useRef(0);

  const setAddedLine = (tempId: string, patch: Partial<AddedLine>) =>
    setAdded((prev) => prev.map((l) => (l.tempId === tempId ? { ...l, ...patch } : l)));
  const removeAddedLine = (tempId: string) =>
    setAdded((prev) => prev.filter((l) => l.tempId !== tempId));

  // Debounced product search (mirrors the draft-editor add-line pattern).
  useEffect(() => {
    if (!showAddSearch) return;
    const q = addQuery.trim();
    if (q.length < 2) { setAddResults([]); return; }
    let cancelled = false;
    setAddSearching(true);
    const t = setTimeout(() => {
      portalProductService
        .searchProducts(q, { limit: 8 })
        .then((r) => { if (!cancelled) setAddResults(r.products); })
        .catch(() => { if (!cancelled) setAddResults([]); })
        .finally(() => { if (!cancelled) setAddSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [addQuery, showAddSearch]);

  const handleAddProduct = async (p: ProductSearchResult) => {
    const itemCode = p.sage_item_code;
    const description = p.udf_fulldesc || p.item_code_desc || itemCode;
    setAddQuery('');
    setAddResults([]);
    setShowAddSearch(false);
    // Resolve an authoritative price via the full waterfall (qty=1). Never seed
    // standard_unit_price as a fallback — force operator entry when the
    // waterfall can't resolve a price (the AU00249 over-price bug class).
    setAddSeeding(true);
    let price = '';
    let priceResolved = false;
    let pricing: LineItemPricingContext | null = null;
    try {
      const resp = customerNo
        ? await portalProductService.lookupProductWithPricing(
            itemCode, customerNo, '1', order.id as string,
          )
        : null;
      const resolved = resp?.resolved;
      if (resolved && resolved.source !== 'investigation' && resolved.resolved_price != null) {
        price = String(resolved.resolved_price);
        priceResolved = true;
      }
      // Keep the options even on investigation: the rep may still pick a
      // listed source (e.g. Std) from the badge instead of typing a price.
      // No badge at all when the backend sent no classification (older API).
      if (resolved && (resolved.current_source || resolved.available_sources?.length)) {
        pricing = pricingItemFromLookup(itemCode, '1', resolved);
      }
    } catch {
      // fall through to operator-entry (price stays blank)
    } finally {
      setAddSeeding(false);
    }
    setAdded((prev) => [
      ...prev,
      {
        tempId: `new-${nextTempId.current++}`,
        itemCode,
        description,
        qty: '1',
        price,
        priceResolved,
        pricing,
        unitWeight: parseWeight(resolveShipWeight(p)),
      },
    ]);
  };

  const handleShipToPicked = (addr: ShipToResult) => {
    setShipToDraft({
      ship_to_code: addr.ship_to_code,
      ship_to_name: addr.ship_to_name,
      address1: addr.address1,
      address2: addr.address2 || '',
      address3: addr.address3 || '',
      city: addr.city,
      state: addr.state,
      zip_code: addr.zip_code,
      warehouse_code: addr.warehouse_code || '',
    });
    setShowShipToPicker(false);
    setShipToCreatedNote(null);
  };

  // Shared NewSageShipToForm handles the POST (incl. the overridable
  // address-warning "create anyway" flow); this just stages the created code.
  const handleShipToCreated = (addr: CreatedShipTo) => {
    setShipToDraft({
      ship_to_code: addr.ship_to_code,
      ship_to_name: addr.ship_to_name,
      address1: addr.address1,
      address2: addr.address2,
      address3: addr.address3,
      city: addr.city,
      state: addr.state,
      zip_code: addr.zip_code,
    });
    setShowNewShipTo(false);
    setShipToCreatedNote(
      `New address queued to Sage as code ${addr.ship_to_code}` +
        (addr.tax_schedule
          ? ` (tax schedule ${addr.tax_schedule}).`
          : '. ⚠ No tax schedule could be derived — set one on the ship-to in Sage or orders using it will fail to import.') +
        ' It will also be applied to this order when you push the changes below.',
    );
    // Refresh the audit list so the ship-to create shows immediately.
    portalOrderService.getSageWrites(order.id).then(setWrites).catch(() => {});
  };

  const { payload, changes } = useMemo(() => {
    const lineUpdates: SageLineUpdatePayload[] = [];
    const lineRemoves: string[] = [];
    const chg: SageFieldChangePayload[] = [];
    for (const l of lines) {
      if (l.removed) {
        // A removed line sends ONLY the removal — any staged qty/price edits
        // on it are moot (the line won't exist).
        lineRemoves.push(l.id);
        chg.push({
          field: `− ${l.itemCode}`,
          old: `qty ${l.origQty} @ $${l.origPrice}`,
          new: 'removed',
          kind: 'line_remove',
        });
        continue;
      }
      const u: SageLineUpdatePayload = { line_item_id: l.id };
      let touched = false;
      // Numeric compare: the saved values carry trailing zeros ("34.0000")
      // while a pick/retype is bare ("34") — not a change.
      if (!sameFieldValue(l.qty, l.origQty)) {
        u.quantity = l.qty === '' ? null : Number(l.qty);
        touched = true;
        chg.push({ field: `${l.itemCode} qty`, old: l.origQty, new: l.qty, kind: 'line_qty' });
      }
      if (!sameFieldValue(l.price, l.origPrice)) {
        u.unit_price = l.price === '' ? null : Number(l.price);
        touched = true;
        chg.push({ field: `${l.itemCode} price`, old: l.origPrice, new: l.price, kind: 'line_price' });
      }
      // Sage comments are append-only. Stage the text only when it would
      // actually change something — an addition already contained in the
      // existing comment is discarded by Sage while still reporting success,
      // so sending it would put a lie in the change summary.
      const existing = existingCommentOf(l.id);
      const outcome = sageCommentOutcome(existing, l.commentAdd);
      if (outcome.effect === 'set' || outcome.effect === 'append') {
        u.sage_comment_text = l.commentAdd.trim();
        touched = true;
        chg.push({
          field: `${l.itemCode} Sage comment`,
          old: existing || '—',
          new: outcome.result,
          kind: 'line_comment',
        });
      }
      if (touched) lineUpdates.push(u);
    }
    const header: Record<string, unknown> = {};
    if (shipVia !== origShipVia) {
      header.ship_via = shipVia || null;
      chg.push({ field: 'ship_via', old: origShipVia, new: shipVia, kind: 'field' });
    }
    if (poNumber !== origPo) {
      header.po_number = poNumber || null;
      chg.push({ field: 'po_number', old: origPo, new: poNumber, kind: 'field' });
    }
    if (udfBl !== origUdfBl) {
      header.udf_bl = udfBl;
      chg.push({
        field: 'udf_bl',
        old: billOfLadingLabel(origUdfBl),
        new: billOfLadingLabel(udfBl),
        kind: 'field',
      });
    }
    if (warehouseCode !== origWarehouse) {
      header.warehouse_code = warehouseCode || '000';
      chg.push({
        field: 'warehouse_code',
        old: warehouseLabel(origWarehouse),
        new: warehouseLabel(warehouseCode || '000'),
        kind: 'field',
      });
    }
    if (shipToDraft && shipToDraft.ship_to_code !== origShipTo?.ship_to_code) {
      header.ship_to_address = shipToDraft;
      chg.push({
        field: 'ship_to',
        old: shipToLabel(origShipTo),
        new: shipToLabel(shipToDraft),
        kind: 'field',
      });
    }
    // New lines: rows with an item + qty become a real add. A blank price is
    // allowed — the push asks "you didn't add a price, send it anyway?" and
    // the server then applies it as an explicit $0.00 (kanban dc8caa8d).
    const lineAdds: SageLineAddPayload[] = [];
    for (const a of added) {
      if (!a.itemCode || a.qty === '') continue;
      const entry: SageLineAddPayload = {
        item_code: a.itemCode,
        item_description: a.description,
        quantity: Number(a.qty),
      };
      if (a.price !== '') entry.unit_price = Number(a.price);
      lineAdds.push(entry);
      // A $0 add is sent to Sage as an EXPLICIT UnitPrice 0 (a declared
      // no-charge line) — name it so the operator sees what they declared.
      const priceText =
        a.price === ''
          ? 'no price'
          : `$${a.price}${Number(a.price) === 0 ? ' (no charge)' : ''}`;
      chg.push({
        field: `+ ${a.itemCode}`,
        old: '—',
        new: `qty ${a.qty} @ ${priceText}`,
        kind: 'line_add',
      });
    }
    const p: SageOrderEditPayload = { line_updates: lineUpdates, changes: chg };
    if (lineAdds.length) p.line_adds = lineAdds;
    if (lineRemoves.length) p.line_removes = lineRemoves;
    if (Object.keys(header).length) p.header = header;
    return { payload: p, changes: chg };
  }, [lines, shipVia, poNumber, origShipVia, origPo, udfBl, origUdfBl, warehouseCode, origWarehouse, shipToDraft, origShipTo, added]);

  // Added rows that aren't yet complete (blocks push until a qty is in).
  const incompleteAdds = added.filter((a) => a.itemCode && a.qty === '');
  // Added rows the operator left unpriced — pushing asks for confirmation.
  const unpricedAdds = added.filter((a) => a.itemCode && a.qty !== '' && a.price === '');

  // Removing every line is not a valid edit — a Sage order must keep at
  // least one line (the backend refuses it too; this guard just explains
  // it before the push). Completed staged adds count toward the remainder.
  const completedAdds = added.filter((a) => a.itemCode && a.qty !== '');
  const wouldEmptyOrder =
    lines.length > 0 &&
    lines.every((l) => l.removed) &&
    completedAdds.length === 0;

  // ── Staged line set → live weight ──
  const stagedLines = useMemo(
    () => [
      ...lines
        .filter((l) => !l.removed)
        .map((l) => ({
          item_code: l.itemCode,
          quantity: l.qty === '' ? null : Number(l.qty),
        })),
      ...completedAdds.map((a) => ({
        item_code: a.itemCode,
        quantity: Number(a.qty),
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, added],
  );

  // Instant local product-weight sums (no skid weight — parcel/LTL judgement
  // still works because both sides use the same basis). Null when any line's
  // unit weight is unknown, so we never show a confidently-wrong total.
  const localWeights = useMemo(() => {
    const sum = (
      rows: { item_code: string; quantity: number | null; unitWeight?: number | null }[],
    ): number | null => {
      let total = 0;
      for (const r of rows) {
        const w = r.unitWeight ?? unitWeights[r.item_code] ?? null;
        const lineW = calculateLineWeight(r.quantity, w);
        if (lineW == null) return null;
        total += lineW;
      }
      return total;
    };
    const before = sum(
      lines.map((l) => ({
        item_code: l.itemCode,
        quantity: l.origQty === '' ? null : Number(l.origQty),
      })),
    );
    const after = sum([
      ...lines
        .filter((l) => !l.removed)
        .map((l) => ({
          item_code: l.itemCode,
          quantity: l.qty === '' ? null : Number(l.qty),
        })),
      ...completedAdds.map((a) => ({
        item_code: a.itemCode,
        quantity: Number(a.qty),
        unitWeight: a.unitWeight,
      })),
    ]);
    return { before, after };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, added, unitWeights]);

  // Authoritative staged weight: debounce the staged set, then ask Sage's
  // freight engine (same envelope the saved baseline uses). Results for a
  // stale staged set are dropped. An unchanged staged set (including edits
  // reverted back to the original) reuses the saved baseline — no API call.
  const origDigest = useMemo(
    () =>
      JSON.stringify(
        orderDetail.line_items.map((li) => ({
          item_code: li.item_code,
          quantity: li.quantity != null ? Number(li.quantity) : null,
        })),
      ),
    [orderDetail.line_items],
  );
  const stagedDigest = JSON.stringify(stagedLines);
  const debouncedDigest = useDebouncedValue(stagedDigest, 600);
  useEffect(() => {
    if (debouncedDigest === origDigest) {
      setPreviewWeight(savedWeight);
      setPreviewLoading(false);
      return;
    }
    const staged: { item_code: string; quantity: number | null }[] =
      JSON.parse(debouncedDigest);
    if (staged.length === 0) { setPreviewWeight(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    portalOrderService
      .getWeightPreview(order.id, staged)
      .then((resp) => { if (!cancelled) setPreviewWeight(resp); })
      .catch(() => { if (!cancelled) setPreviewWeight(null); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedDigest, origDigest, order.id, savedWeight]);

  // Prefer the Sage skid-inclusive pair; fall back to the local pair. Never
  // mix bases (a skid-inclusive "before" vs product-only "after" would show
  // a phantom delta).
  const sageBefore = savedWeight?.success ? savedWeight.total_weight : null;
  const sageAfter = previewWeight?.success ? previewWeight.total_weight : null;
  const usingSagePair = sageBefore != null && sageAfter != null;
  const weightBefore = usingSagePair ? sageBefore : localWeights.before;
  const weightAfter = usingSagePair ? sageAfter : localWeights.after;
  const weightAdvisory = weightThresholdAdvisory(weightBefore, weightAfter);

  // Pricing context by item code (existing lines only — a line added in this
  // session isn't in the order's context yet; its price comes from the
  // waterfall seed in handleAddProduct and stays hand-editable).
  const pricingByItem = useMemo(() => {
    const map: Record<string, LineItemPricingContext> = {};
    for (const li of pricingCtx?.line_items ?? []) map[li.item_code] = li;
    return map;
  }, [pricingCtx]);

  // /freight-quotes rates the SAVED order (its ship-to + lines as last
  // pushed). Flag when the staged edit would change the shipment so the
  // operator knows the quotes don't reflect what they're about to push.
  const hasUnpushedShipmentEdits =
    stagedDigest !== origDigest ||
    (shipToDraft != null && shipToDraft.ship_to_code !== origShipTo?.ship_to_code);

  // The parts of the staged edit the dock cares about (empty = price /
  // comment / PO only, no override needed).
  const shipmentAffecting = useMemo(() => shipmentAffectingChanges(changes), [changes]);
  const stagedNeedsOverride = Boolean(staging?.staged) && shipmentAffecting.length > 0;
  // The email is the point of the override: at least one address must be ticked.
  const noticeRecipientsValid = noticeRecipients.length > 0;

  const [noPricePrompt, setNoPricePrompt] = useState(false);
  // Soft server advisories held back with requires_acknowledgment (today: a
  // two-part kit's COMPONENT added without its kit parent — AU02050). The
  // edit was NOT applied; "Push anyway" re-sends with acknowledge_warnings.
  const [ackWarnings, setAckWarnings] = useState<SubmissionFieldError[] | null>(null);
  // Remembered so "Push anyway" re-sends the same no-price confirmation.
  const [ackSendWithoutPrice, setAckSendWithoutPrice] = useState(false);

  // Remembered so the staged override re-sends an already-given kit ack.
  const [ackKitAcknowledged, setAckKitAcknowledged] = useState(false);

  const handlePush = async (
    opts: {
      sendWithoutPrice?: boolean;
      acknowledgeWarnings?: boolean;
      stagedOverride?: boolean;
    } = {},
  ) => {
    if (unpricedAdds.length > 0 && !opts.sendWithoutPrice) {
      // "You didn't add a price for this item — please confirm" (review
      // y4yvpz): the line would land in Sage at $0.00, so say so first.
      setNoPricePrompt(true);
      return;
    }
    if (stagedNeedsOverride && !opts.stagedOverride) {
      // The dock has already packed this order and the edit changes the
      // shipment: stop here, say so, and make the override a deliberate
      // click (Hadi, 2026-10-16). The server enforces the same hold.
      setStagedPrompt(true);
      return;
    }
    setNoPricePrompt(false);
    setStagedPrompt(false);
    setSubmitting(true);
    setError(null);
    try {
      const priced: SageOrderEditPayload = opts.sendWithoutPrice
        ? {
            ...payload,
            line_adds: (payload.line_adds ?? []).map((la) =>
              la.unit_price === undefined ? { ...la, no_price_confirmed: true } : la,
            ),
          }
        : payload;
      const acked: SageOrderEditPayload = opts.acknowledgeWarnings
        ? { ...priced, acknowledge_warnings: true }
        : priced;
      const toSend: SageOrderEditPayload = opts.stagedOverride
        ? { ...acked, staged_override: true, staged_notice_recipients: noticeRecipients }
        : acked;
      const res = await portalOrderService.applySageEdit(order.id, toSend);
      if (res.success) {
        setAckWarnings(null);
        setPushedCount(changes.length);
        setDone(true);
        onSaved();
      } else if (res.requires_acknowledgment && res.staging) {
        // Server-side staged hold (the modal's own read may have been stale
        // or unavailable): show the staged facts it returned and ask.
        setStaging((prev) => ({ ...(prev ?? {}), ...res.staging } as OrderStagingStatus));
        if (!noticeTouched.current && noticeRecipients.length === 0) {
          setNoticeRecipients(res.staging.notice_recipients ?? []);
        }
        setStagedPrompt(true);
        setAckSendWithoutPrice(Boolean(opts.sendWithoutPrice));
        setAckKitAcknowledged(Boolean(opts.acknowledgeWarnings));
      } else if (res.requires_acknowledgment) {
        setAckWarnings(res.submission_warnings ?? []);
        setAckSendWithoutPrice(Boolean(opts.sendWithoutPrice));
      } else {
        setError(res.error_message || 'Sage update failed');
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const stagedShipTo = shipToDraft ?? origShipTo;

  return (
    <div style={s.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.head}>
          <div>
            <div style={s.title}>✎ Edit in Sage</div>
            <div style={s.sub}>
              {order.sage_order_no} · {order.customer_name} · pushes to Sage (not a draft edit)
            </div>
          </div>
          <button
            style={s.btn}
            onClick={onClose}
            type="button"
            aria-label="Close"
            data-action="order_sage_edit_close"
            data-target-order-id={order.id}
            data-outcome="close"
          >
            ✕
          </button>
        </div>

        <div style={s.body}>
          {done ? (
            <div style={{ ...s.diff, color: '#059669', fontSize: 14 }}>
              ✅ Pushed {pushedCount} change{pushedCount === 1 ? '' : 's'} to Sage for {order.sage_order_no}.
            </div>
          ) : (
            <>
              {staging?.staged && (
                <div
                  style={{
                    ...s.diff,
                    color: '#7c2d12',
                    background: '#fff4e5',
                    border: '1px solid #fcd34d',
                    borderRadius: 8,
                    padding: '8px 10px',
                    margin: '0 0 10px',
                  }}
                  data-testid="staged-banner"
                  role="status"
                >
                  <div style={{ fontWeight: 600 }}>
                    📦 Already staged on the dock
                    {stagedSinceLabel(staging.staged_at) ? ` since ${stagedSinceLabel(staging.staged_at)}` : ''}
                    {shortDateLabel(staging.est_ship_date) ? ` · ships ${shortDateLabel(staging.est_ship_date)}` : ''}
                    {staging.ship_via ? ` via ${staging.ship_via}` : ''}
                  </div>
                  <div style={{ marginTop: 2 }}>
                    A change to the carrier, ship-to, warehouse, dates or lines needs an
                    override, and shipping gets an email with the change so the pallet is
                    re-checked. Price and comment edits go through as usual.
                  </div>
                </div>
              )}
              <div style={s.row}>
                <span style={s.label}>Ship-via</span>
                <input style={s.input} value={shipVia} onChange={(e) => setShipVia(e.target.value)} aria-label="Ship via" />
                <button
                  style={{ ...s.smallBtn, color: '#2563eb', borderColor: '#bfdbfe' }}
                  type="button"
                  onClick={() => setShowFreight(true)}
                  title="Fetch live carrier quotes for this order's ship-to and lines"
                >
                  Get Quotes
                </button>
              </div>
              {hasUnpushedShipmentEdits && (
                <div
                  style={{ ...s.diff, color: '#b45309', marginLeft: 130, marginTop: 0 }}
                  data-testid="freight-quote-staleness-note"
                >
                  ⚠ Freight quotes rate the order as saved in Sage. Your staged
                  line / ship-to changes are not included — push them first, then
                  re-run quotes.
                </div>
              )}
              <div style={s.row} data-target-order-id={order.id}>
                <span style={s.label}>Bill of Lading</span>
                <select
                  style={{ ...s.input, flex: '0 0 150px' }}
                  value={udfBl}
                  onChange={(e) => setUdfBl(e.target.value)}
                  aria-label="Bill of lading"
                  data-testid="sage-edit-udf-bl"
                  data-action="sage_edit_bill_of_lading_pick"
                  data-record-value="udf_bl"
                >
                  {BILL_OF_LADING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {udfBl !== origUdfBl && (
                  <span style={s.diff} data-testid="sage-edit-udf-bl-diff">
                    {billOfLadingLabel(origUdfBl)} → {billOfLadingLabel(udfBl)}
                  </span>
                )}
              </div>
              {customerPaysCarrier(udfBl) && (
                <div
                  style={{ ...s.diff, color: '#6b7280', marginLeft: 130, marginTop: 0 }}
                  data-testid="sage-edit-udf-bl-note"
                >
                  {billOfLadingLabel(udfBl)}: the customer pays the carrier. The Sage BOL
                  prints {udfBl} and the invoice queues with no freight.
                </div>
              )}
              <div style={s.row}>
                <span style={s.label}>Customer PO</span>
                <input style={s.input} value={poNumber} onChange={(e) => setPoNumber(e.target.value)} aria-label="Customer PO" />
              </div>

              <div style={s.row}>
                <span style={s.label}>Warehouse</span>
                <SearchableDropdown
                  value={warehouseCode}
                  onChange={(next) => setWarehouseCode(next || '000')}
                  items={warehouseItems}
                  placeholder="Type code or name..."
                  maxLength={3}
                  inputStyle={s.input}
                  testId="sage-edit-warehouse"
                  highlightedLabel="Active"
                  otherLabel="Other"
                  record={{ action: 'sage_edit_warehouse_pick', valueKey: 'warehouse_code', targets: { 'order-id': order.id } }}
                />
                <span
                  style={{ fontSize: 12, color: '#6b7280' }}
                  data-testid="sage-edit-warehouse-name"
                >
                  {findWarehouseByCode(warehouseCode)?.label ?? 'Unknown warehouse code'}
                </span>
              </div>

              <div style={s.row}>
                <span style={s.label}>Ship-to</span>
                <div style={s.shipToBox} aria-label="Ship-to address">
                  <span style={shipToDraft ? s.neu : undefined}>
                    {shipToLabel(stagedShipTo)}
                  </span>
                  {stagedShipTo?.address1 && (
                    <span style={{ color: '#6b7280', fontSize: 12 }}>
                      {stagedShipTo.ship_to_name ? `${stagedShipTo.ship_to_name} · ` : ''}
                      {[stagedShipTo.address1, stagedShipTo.address2, stagedShipTo.address3].filter(Boolean).join(', ')}
                    </span>
                  )}
                  <button
                    style={s.smallBtn}
                    type="button"
                    onClick={() => { setShowShipToPicker((v) => !v); setShowNewShipTo(false); }}
                  >
                    Change
                  </button>
                  <button
                    style={s.smallBtn}
                    type="button"
                    onClick={() => { setShowNewShipTo((v) => !v); setShowShipToPicker(false); }}
                  >
                    New address…
                  </button>
                  {shipToDraft && (
                    <button
                      style={s.smallBtn}
                      type="button"
                      onClick={() => { setShipToDraft(null); setShipToCreatedNote(null); }}
                    >
                      Revert
                    </button>
                  )}
                </div>
              </div>

              {showShipToPicker && (
                <div style={{ margin: '4px 0 8px 130px' }}>
                  <ShipToSearch
                    customerNo={(order.customer_no as string) ?? null}
                    customerName={(order.customer_name as string) ?? undefined}
                    onSelect={handleShipToPicked}
                    onCustomerMismatch={(_addr, ownerNo, ownerName) => {
                      alert(`This address belongs to ${ownerName} (${ownerNo}), which is not the current customer.`);
                    }}
                    onCancel={() => setShowShipToPicker(false)}
                  />
                </div>
              )}

              {showNewShipTo && (
                <div style={s.panel}>
                  <NewSageShipToForm
                    orderId={order.id}
                    customerNo={(order.customer_no as string) ?? null}
                    cancelLabel="Cancel"
                    onCreated={handleShipToCreated}
                    onCancel={() => setShowNewShipTo(false)}
                  />
                </div>
              )}

              {shipToCreatedNote && (
                <div style={{ ...s.diff, color: '#059669', marginLeft: 130 }}>
                  ✓ {shipToCreatedNote}
                </div>
              )}

              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
                <thead>
                  <tr>
                    <th style={s.th}>Item</th>
                    <th style={s.th}>Description</th>
                    <th style={s.th}>Qty</th>
                    <th style={s.th}>Unit $</th>
                    <th style={s.th} aria-label="Line actions" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <React.Fragment key={l.id}>
                    <tr data-testid={l.removed ? 'removed-line-row' : undefined}>
                      <td style={{ ...s.td, ...(l.removed ? { textDecoration: 'line-through', color: '#9ca3af' } : {}) }}>
                        {l.itemCode}
                      </td>
                      <td style={{ ...s.td, color: l.removed ? '#9ca3af' : '#6b7280', ...(l.removed ? { textDecoration: 'line-through' } : {}) }}>
                        {l.description}
                      </td>
                      {l.removed ? (
                        <td style={{ ...s.td, color: '#dc2626', fontSize: 12 }} colSpan={2}>
                          will be removed
                        </td>
                      ) : (
                        <>
                          <td style={s.td}>
                            <input
                              style={{ ...s.input, width: 70 }}
                              value={l.qty}
                              inputMode="decimal"
                              onChange={(e) => setLine(l.id, { qty: e.target.value })}
                              aria-label={`Qty ${l.itemCode}`}
                            />
                          </td>
                          <td style={s.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input
                                style={{ ...s.input, width: 90 }}
                                value={l.price}
                                inputMode="decimal"
                                onChange={(e) => setLine(l.id, { price: e.target.value })}
                                aria-label={`Price ${l.itemCode}`}
                              />
                              <PricingSourcePicker
                                itemCode={l.itemCode}
                                pricingItem={restagePricingItem(pricingByItem[l.itemCode], l.price)}
                                allowPick
                                onApplyPrice={(price) => setLine(l.id, { price: String(price) })}
                                formatPrice={formatPrice}
                              />
                            </div>
                          </td>
                        </>
                      )}
                      <td style={s.td}>
                        {l.removed ? (
                          <button
                            style={s.smallBtn}
                            type="button"
                            onClick={() => setLine(l.id, { removed: false })}
                            aria-label={`Undo remove ${l.itemCode}`}
                          >
                            Undo
                          </button>
                        ) : (
                          <button
                            style={{ ...s.smallBtn, padding: '4px 8px', color: '#dc2626' }}
                            type="button"
                            onClick={() => setLine(l.id, { removed: true })}
                            aria-label={`Remove ${l.itemCode}`}
                            title="Remove this line from the order"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                    {!l.removed && (
                      <tr data-testid={`sage-comment-row-${l.id}`}>
                        <td />
                        <td colSpan={4} style={{ ...s.td, paddingTop: 0, borderBottom: '1px solid #f1f5f9' }}>
                          {(() => {
                            const existing = existingCommentOf(l.id);
                            if (!existing) return null;
                            return (
                              <div
                                style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 3 }}
                                data-testid={`sage-comment-existing-${l.id}`}
                              >
                                Sage comment now: <span style={{ color: '#374151' }}>{existing}</span>
                                {mirrorSettled && !mirrorReady && (
                                  <span
                                    style={{ color: '#b45309', marginLeft: 6 }}
                                    title="Sage could not be read just now, so this is the last comment the portal sent. Sage may hold more."
                                    data-testid={`sage-comment-stale-${l.id}`}
                                  >
                                    (last sent; Sage not read)
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          <input
                            style={{ ...s.input, width: '100%', maxWidth: 380, fontSize: 12.5 }}
                            value={l.commentAdd}
                            maxLength={2048}
                            placeholder={
                              existingCommentOf(l.id)
                                ? 'Add to the Sage comment…'
                                : 'Add a Sage comment for this line…'
                            }
                            onChange={(e) => setLine(l.id, { commentAdd: e.target.value })}
                            aria-label={`Sage comment ${l.itemCode}`}
                          />
                          {(() => {
                            // Live outcome, so the append-only behaviour (and
                            // the silent no-op on repeated text) is visible
                            // BEFORE the push rather than discovered after.
                            const oc = sageCommentOutcome(existingCommentOf(l.id), l.commentAdd);
                            if (oc.effect === 'none') return null;
                            if (oc.effect === 'already_present') {
                              return (
                                <div
                                  style={{ fontSize: 11.5, color: '#b45309', marginTop: 3 }}
                                  data-testid={`sage-comment-noop-${l.id}`}
                                >
                                  Already in the Sage comment — this would not change anything.
                                </div>
                              );
                            }
                            return (
                              <div
                                style={{ fontSize: 11.5, color: '#059669', marginTop: 3 }}
                                data-testid={`sage-comment-preview-${l.id}`}
                              >
                                Will read: {oc.result}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                  {added.map((a) => (
                    <tr key={a.tempId} data-testid="added-line-row">
                      <td style={{ ...s.td, color: '#059669', fontWeight: 600 }}>
                        + {a.itemCode}
                      </td>
                      <td style={{ ...s.td, color: '#6b7280' }}>
                        {a.description}
                        {!a.priceResolved && a.price === '' && (
                          <span style={{ color: '#b45309', fontSize: 11, marginLeft: 6 }}>
                            (enter price)
                          </span>
                        )}
                        {a.price !== '' && Number(a.price) === 0 && (
                          <span
                            style={{ color: '#b45309', fontSize: 11, marginLeft: 6 }}
                            title="Sent to Sage as an explicit $0.00 — a deliberate no-charge line"
                          >
                            (no charge)
                          </span>
                        )}
                      </td>
                      <td style={s.td}>
                        <input
                          style={{ ...s.input, width: 70 }}
                          value={a.qty}
                          inputMode="decimal"
                          onChange={(e) => setAddedLine(a.tempId, { qty: e.target.value })}
                          aria-label={`Qty new ${a.itemCode}`}
                        />
                      </td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            style={{ ...s.input, width: 90 }}
                            value={a.price}
                            inputMode="decimal"
                            onChange={(e) => setAddedLine(a.tempId, { price: e.target.value })}
                            aria-label={`Price new ${a.itemCode}`}
                          />
                          {/* Same SP / Qt / Std badge + dropdown the saved rows get,
                              fed by the add-time lookup (kanban e271f10d) so the
                              rep can see and switch the source of a new line. */}
                          {a.pricing && (
                            <PricingSourcePicker
                              itemCode={a.itemCode}
                              pricingItem={restagePricingItem(a.pricing, a.price)}
                              allowPick
                              onApplyPrice={(price) =>
                                setAddedLine(a.tempId, { price: String(price) })
                              }
                              formatPrice={formatPrice}
                            />
                          )}
                        </div>
                      </td>
                      <td style={s.td}>
                        <button
                          style={{ ...s.smallBtn, padding: '4px 8px' }}
                          type="button"
                          onClick={() => removeAddedLine(a.tempId)}
                          aria-label={`Remove new ${a.itemCode}`}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ marginTop: 8 }}>
                {!showAddSearch ? (
                  <button
                    style={{ ...s.smallBtn, color: '#2563eb', borderColor: '#bfdbfe' }}
                    type="button"
                    onClick={() => setShowAddSearch(true)}
                  >
                    + Add line item
                  </button>
                ) : (
                  <div style={{ ...s.panel, marginLeft: 0 }}>
                    <input
                      style={{ ...s.input, width: '100%', boxSizing: 'border-box' }}
                      value={addQuery}
                      autoFocus
                      placeholder="Search item code or description…"
                      onChange={(e) => setAddQuery(e.target.value)}
                      aria-label="Add line item search"
                    />
                    {addSeeding && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                        Resolving price…
                      </div>
                    )}
                    {addQuery.trim().length >= 2 && (
                      <div style={{ marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
                        {addSearching ? (
                          <div style={{ fontSize: 12, color: '#6b7280' }}>Searching…</div>
                        ) : addResults.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#9ca3af' }}>No matches.</div>
                        ) : (
                          addResults.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              data-testid="add-line-result"
                              onClick={() => handleAddProduct(p)}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                padding: '6px 8px', border: 'none', borderRadius: 6,
                                background: 'transparent', cursor: 'pointer', fontSize: 12.5,
                              }}
                            >
                              <strong>{p.sage_item_code}</strong>{' '}
                              <span style={{ color: '#6b7280' }}>
                                {p.udf_fulldesc || p.item_code_desc || ''}
                              </span>
                              <KitComponentBadge product={p} />
                              <BrandAliasBadge product={p} />
                            </button>
                          ))
                        )}
                      </div>
                    )}
                    <div style={{ marginTop: 6 }}>
                      <button
                        style={s.smallBtn}
                        type="button"
                        onClick={() => { setShowAddSearch(false); setAddQuery(''); setAddResults([]); }}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div
                style={{
                  marginTop: 12, padding: '8px 10px', borderRadius: 8,
                  background: '#f9fafb', border: '1px solid #e5e7eb',
                }}
                data-testid="weight-panel"
              >
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Shipment weight{usingSagePair ? ' (Sage estimate, incl. skids)' : ' (product weight only)'}
                  {previewLoading ? ' · updating…' : ''}
                </div>
                {weightBefore == null && weightAfter == null ? (
                  <div style={{ fontSize: 12.5, color: '#9ca3af', marginTop: 2 }}>
                    Weight unavailable (missing unit weights and no freight estimate).
                  </div>
                ) : (
                  <div style={{ fontSize: 13.5, color: '#111827', marginTop: 2, fontWeight: 600 }}>
                    {weightBefore != null ? `${Math.round(weightBefore).toLocaleString()} lb` : '—'}
                    {' → '}
                    <span style={weightAfter != null && weightBefore != null && weightAfter !== weightBefore ? s.neu : undefined}>
                      {weightAfter != null ? `${Math.round(weightAfter).toLocaleString()} lb` : '—'}
                    </span>
                    {weightBefore != null && weightAfter != null && weightAfter !== weightBefore && (
                      <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>
                        ({weightAfter > weightBefore ? '+' : '−'}
                        {Math.round(Math.abs(weightAfter - weightBefore)).toLocaleString()} lb)
                      </span>
                    )}
                  </div>
                )}
                {weightAdvisory && (
                  <div
                    style={{ fontSize: 12.5, color: '#b45309', marginTop: 4 }}
                    data-testid="weight-advisory"
                  >
                    ⚠ {weightAdvisory}
                  </div>
                )}
              </div>

              {ackWarnings && (
                <div
                  style={{ ...s.diff, color: '#7c2d12', background: '#fff4e5', padding: '8px 10px', marginTop: 10 }}
                  data-testid="kit-component-prompt"
                  role="alert"
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    ⚠ Not pushed yet — please check the line(s) below:
                  </div>
                  <ul style={{ margin: '0 0 6px 18px', padding: 0 }}>
                    {ackWarnings.map((w, i) => (
                      <li key={`${w.field}-${i}`}>{w.message}</li>
                    ))}
                  </ul>
                  <span style={{ display: 'inline-flex', gap: 8 }}>
                    <button
                      style={{ ...s.smallBtn, background: '#b45309', color: '#fff', borderColor: '#b45309' }}
                      type="button"
                      onClick={() =>
                        void handlePush({
                          sendWithoutPrice: ackSendWithoutPrice,
                          acknowledgeWarnings: true,
                        })
                      }
                      disabled={submitting}
                    >
                      Push anyway
                    </button>
                    <button style={s.smallBtn} type="button" onClick={() => setAckWarnings(null)}>
                      Go back and fix
                    </button>
                  </span>
                </div>
              )}

              {stagedPrompt && staging?.staged && (
                <div
                  style={{ ...s.diff, color: '#7c2d12', background: '#fff4e5', padding: '8px 10px', marginTop: 10 }}
                  data-testid="staged-override-prompt"
                  role="alert"
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    ⚠ Not pushed yet — {order.sage_order_no} is already staged
                    {stagedSinceLabel(staging.staged_at) ? ` (since ${stagedSinceLabel(staging.staged_at)})` : ''}.
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    This edit changes {shipmentAffecting.join(', ') || 'the shipment'}. Overriding
                    pushes it to Sage and sends the change by email, with you cc'd, so the
                    pallet gets re-staged.
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <StagedNoticeRecipientsList
                      orderId={order.id}
                      selected={noticeRecipients}
                      onSelectedChange={(emails) => { noticeTouched.current = true; setNoticeRecipients(emails); }}
                    />
                  </div>
                  {!noticeRecipientsValid && (
                    <div style={{ color: '#b91c1c', marginBottom: 6 }} data-testid="staged-notice-to-error">
                      Tick at least one address to send the email to.
                    </div>
                  )}
                  <span style={{ display: 'inline-flex', gap: 8 }}>
                    <button
                      style={{ ...s.smallBtn, background: '#b45309', color: '#fff', borderColor: '#b45309' }}
                      type="button"
                      data-action="order_sage_edit_staged_override"
                      data-target-order-id={order.id}
                      data-outcome="request"
                      onClick={() =>
                        void handlePush({
                          sendWithoutPrice: ackSendWithoutPrice,
                          acknowledgeWarnings: ackKitAcknowledged || undefined,
                          stagedOverride: true,
                        })
                      }
                      disabled={submitting || !noticeRecipientsValid}
                    >
                      Override and push (sends the email)
                    </button>
                    <button
                      style={s.smallBtn}
                      type="button"
                      data-action="order_sage_edit_staged_override_cancel"
                      data-target-order-id={order.id}
                      data-outcome="none"
                      onClick={() => setStagedPrompt(false)}
                    >
                      Go back
                    </button>
                  </span>
                </div>
              )}

              {noPricePrompt && unpricedAdds.length > 0 && (
                <div
                  style={{ ...s.diff, color: '#7c2d12', background: '#fff4e5', padding: '8px 10px', marginTop: 10 }}
                  data-testid="no-price-prompt"
                >
                  You didn't add a price for {unpricedAdds.map((a) => a.itemCode).join(', ')}.
                  Send it without a price? Sage will store the line at $0.00.
                  <span style={{ display: 'inline-flex', gap: 8, marginLeft: 10 }}>
                    <button
                      style={{ ...s.smallBtn, background: '#b45309', color: '#fff', borderColor: '#b45309' }}
                      type="button"
                      onClick={() => void handlePush({ sendWithoutPrice: true })}
                      disabled={submitting}
                    >
                      Yes, send without a price
                    </button>
                    <button style={s.smallBtn} type="button" onClick={() => setNoPricePrompt(false)}>
                      Go back
                    </button>
                  </span>
                </div>
              )}
              {wouldEmptyOrder && (
                <div style={{ ...s.diff, color: '#dc2626', marginTop: 10 }}>
                  ⚠ An order must keep at least one line item — undo a removal
                  (or add a replacement line) before pushing. Cancel the order
                  instead if nothing on it should ship.
                </div>
              )}

              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                  Pending changes ({changes.length})
                </div>
                {changes.length === 0 ? (
                  <div style={{ ...s.diff, color: '#9ca3af' }}>No changes yet.</div>
                ) : (
                  changes.map((c, i) => (
                    <div key={i} style={s.diff}>
                      <strong>{c.field}</strong>: <span style={s.old}>{String(c.old) || '—'}</span>{' → '}
                      <span style={s.neu}>{String(c.new) || '—'}</span>
                    </div>
                  ))
                )}
              </div>

              {error && (
                <div style={{ ...s.diff, color: '#dc2626', marginTop: 10 }}>⚠ {error}</div>
              )}

              <div style={{ marginTop: 16, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                <button
                  style={{ ...s.smallBtn, border: 'none', background: 'none', color: '#2563eb', padding: 0 }}
                  type="button"
                  onClick={() => setShowHistory((v) => !v)}
                  aria-expanded={showHistory}
                >
                  {showHistory ? '▾' : '▸'} Sage edit history
                  {writes ? ` (${writes.length})` : ''}
                </button>
                {showHistory && (
                  <div style={{ marginTop: 8 }}>
                    {!writes || writes.length === 0 ? (
                      <div style={{ ...s.diff, color: '#9ca3af' }}>
                        No Sage edits recorded for this order yet.
                      </div>
                    ) : (
                      writes.map((w) => {
                        const c = HIST_STATUS_COLORS[w.status] ?? HIST_STATUS_COLORS.pending;
                        return (
                          <div
                            key={w.id}
                            style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}
                            data-testid="sage-write-row"
                          >
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ ...s.histStatus, background: c.bg, color: c.fg }}>
                                {w.status}
                              </span>
                              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
                                {KIND_LABELS[w.kind] ?? w.kind}
                                {w.kind !== 'order_update' && w.entity_ref ? ` · ${w.entity_ref}` : ''}
                              </span>
                              <span style={{ fontSize: 11.5, color: '#6b7280' }}>
                                {new Date(w.created_at).toLocaleString()}
                                {' · '}{SOURCE_LABELS[w.source] ?? w.source}
                                {w.created_by_email ? ` · ${w.created_by_email}` : ''}
                              </span>
                            </div>
                            {(w.changes ?? []).map((c2, i) => {
                              const settled = formatSettledLinePrice(c2);
                              return (
                                <div key={i} style={{ ...s.diff, marginLeft: 4 }}>
                                  <strong>{c2.field}</strong>:{' '}
                                  {settled !== null ? (
                                    <span style={s.neu}>{settled}</span>
                                  ) : (
                                    <>
                                      <span style={s.old}>{historyValue(c2.old)}</span>{' → '}
                                      <span style={s.neu}>{historyValue(c2.new)}</span>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                            {w.error_message && (
                              <div style={{ ...s.diff, color: '#dc2626', marginLeft: 4 }}>
                                ⚠ {w.error_message}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <FreightQuoteModal
          isOpen={showFreight}
          onClose={() => setShowFreight(false)}
          onSelect={(code) => setShipVia(code)}
          orderId={order.id}
          currentShipVia={shipVia || null}
        />

        <div style={s.actions}>
          <button
            style={s.btn}
            onClick={onClose}
            type="button"
            data-action="order_sage_edit_close"
            data-target-order-id={order.id}
          >
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button
              style={{
                ...s.btn, ...s.primary,
                opacity: changes.length === 0 || incompleteAdds.length > 0 || wouldEmptyOrder || submitting ? 0.5 : 1,
              }}
              onClick={() => void handlePush()}
              disabled={changes.length === 0 || incompleteAdds.length > 0 || wouldEmptyOrder || submitting}
              type="button"
              // A stable name whatever the count in the label, keyed by the
              // order it writes back to (kanban f0b295e8).
              data-action="order_push_changes_to_sage"
              data-target-order-id={order.id}
              data-outcome="request"
              title={
                wouldEmptyOrder
                  ? 'An order must keep at least one line item'
                  : incompleteAdds.length > 0
                    ? 'Fill in the qty for the new line(s) first'
                    : undefined
              }
            >
              {submitting
                ? 'Pushing…'
                : stagedNeedsOverride
                  ? `Push ${changes.length} change${changes.length === 1 ? '' : 's'} (staged order — override needed)`
                  : `Push ${changes.length} change${changes.length === 1 ? '' : 's'} to Sage`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SageEditModal;
